/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable lit/prefer-static-styles */
import { localized, msg } from "@lit/localize";
import type { TemplateResult } from "lit";
import { css, html, LitElement, nothing, unsafeCSS } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import type { WhModalElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-modal";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-modal";

import type { ServiceAppointmentInfo, ServiceCard } from "../types/content";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-services-modal": WebKioskServicesModalElement;
  }
}

@localized()
@customElement("web-kiosk-services-modal")
export class WebKioskServicesModalElement extends LitElement {
  @property() card: ServiceCard | undefined = undefined;

  @property() appointment: ServiceAppointmentInfo | undefined = undefined;

  @query("wh-modal") modal: WhModalElement | undefined;

  show(): void {
    this.modal?.showModal();
  }

  hide(): void {
    this.modal?.hideModal();
  }

  renderModalHeader(
    card: ServiceCard | undefined
  ): TemplateResult | typeof nothing {
    if (!card) {
      return nothing;
    }

    if (!card.headerColor) {
      return html`<label id="close" slot="close-button-content">
        <img
          src="assets/shared/close.svg"
          alt=${msg("Close", { id: "services-modal-close" })}
        />
        ${msg("Close", { id: "services-modal-close" })}
      </label>`;
    }

    return html` <span id="custom-header" slot="modal-header">
        <img src=${card.headerLogo} alt=${card.title} />
        <button @click=${this.hide}>
          <wh-icon name="close"></wh-icon> ${msg("Close", {
            id: "services-modal-close",
          })}
        </button>
      </span>
      <style>
        wh-modal::part(header) {
          border-top-left-radius: inherit;
          border-top-right-radius: inherit;
          color: ${unsafeCSS(card.headerColor)};
          background-color: ${unsafeCSS(card.headerBackgroundColor)};
        }

        wh-modal::part(title) {
          padding: 0;
        }

        #custom-header {
          display: flex;
          width: 100%;
          justify-content: space-between;
          height: 104px;
          padding: 0 25px;
          align-items: center;
        }

        #custom-header img {
          margin-left: var(--web-kiosk-dimension-spacer-m, 32px);
        }

        #custom-header button {
          display: flex;
          align-items: center;
          color: ${unsafeCSS(card.headerColor)};
          font-family: var(--web-kiosk-font, "Source Sans Pro");
          font-size: 33px;
          font-weight: 700;
          line-height: 40px;
          background: none;
          border: none;
          cursor: pointer;
        }

        #custom-header button wh-icon {
          font-size: 1em;
          margin-right: var(--web-kiosk-dimension-spacer-xs, 16px);
        }
      </style>`;
  }

  renderAppointmentInfo(
    appointment: ServiceAppointmentInfo | undefined
  ): TemplateResult | typeof nothing {
    if (!appointment) {
      return nothing;
    }

    return html`<section id="appointment">
      <dl>
        <dt>
          <strong>${appointment.title}</strong>
        </dt>
        <dd>${appointment.phoneNumber}</dd>
      </dl>
    </section>`;
  }

  render(): TemplateResult {
    const { card, appointment } = this;

    return html` <wh-modal id="service-modal" flat fitContent>
      ${this.renderModalHeader(card)}
      <div id="modal-inner">
        <section id="icon">
          <img src=${card?.icon} alt=${card?.title} />
        </section>
        <section id="texts">
          <h3>${card?.title}</h3>
          <h4>${card?.subTitle}</h4>
          <p><strong>${card?.highlightedText}</strong></p>
          <p>${unsafeHTML(card?.additionalText)}</p>
          <p class="legal">${card?.legalDisclaimer}</p>
        </section>
        ${this.renderAppointmentInfo(appointment)}
      </div>
    </wh-modal>`;
  }

  static styles = css`
    :host {
      --web-kiosk-text-color: #323264;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-dimension-spacer-xxs: 8px;
      --web-kiosk-dimension-spacer-xs: 16px;
      --web-kiosk-dimension-spacer-s: 24px;
      --web-kiosk-dimension-spacer-m: 32px;
      --web-kiosk-font: "Source Sans Pro", helvetica, arial;
    }

    wh-modal {
      --wh-modal-background-color: #f5f5f0;
    }

    #close {
      display: block;
      color: var(--web-kiosk-link-color, hotpink);
      background: none;
      padding: var(--web-kiosk-dimension-spacer-s, 24px)
        var(--web-kiosk-dimension-spacer-xs, 16px);
      border: none;
      /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
      font-family: "Source Sans Pro";
      font-size: 33px;
      font-weight: 700;
      line-height: 40px;
    }

    #close:hover {
      cursor: pointer;
    }

    #close img {
      margin-right: var(--web-kiosk-dimension-spacer-xxs, 8px);
    }

    #modal-inner {
      display: flex;
      min-height: 595px;
      max-width: 1305px;
      padding-top: 7vw;
      padding-left: 8vw;
      padding-bottom: 36px;
    }

    wh-modal::part(overlay) {
      background-color: #00000080;
    }

    #icon {
      width: 8%;
      flex: 1 0 8%;
    }

    #icon img {
      width: 110px;
    }

    #texts {
      width: 58%;
      flex: 1 0 58%;
    }

    #texts h3 {
      margin: 0 0 var(--web-kiosk-dimension-spacer-xs, 16px)
        var(--web-kiosk-dimension-spacer-xs, 16px);
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 48px;
      line-height: 62px;
    }

    #texts h4 {
      margin: 0 0 var(--web-kiosk-dimension-spacer-s, 24px)
        var(--web-kiosk-dimension-spacer-xs, 16px);
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
      font-weight: 600;
      line-height: 42px;
    }

    #texts strong {
      font-weight: 600;
    }

    #texts p {
      margin-left: var(--web-kiosk-dimension-spacer-xs, 16px);
      font-size: 28px;
      line-height: 48px;
      font-weight: 400;
    }

    #texts p.legal {
      font-size: 20px;
      line-height: 28px;
    }

    #appointment {
      display: flex;
      flex: 1 0 34%;
      width: 34%;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background: url("assets/services/bg/blob.svg") no-repeat;
      background-size: contain;
      background-position: 100%;
    }

    #appointment dl {
      display: flex;
      padding-left: 3vw;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    #appointment dt {
      margin-bottom: var(--web-kiosk-dimension-spacer-s, 24px);
      font-size: var(--web-kiosk-dimension-spacer-s, 24px);
      line-height: 30px;
    }

    #appointment dd {
      margin-bottom: 56px;
      margin-left: 0;
      font-size: 28px;
      line-height: 48px;
    }

    #qr-code {
      height: 128px;
    }
  `;
}
